import { Id, IsoDate, IsoDateTime, defineAction, defineEvent, z } from '@mkcore/shared';
import { portalPermissions as P } from './permissions';

// Namespace portal (docs/04-domeinen/portal.md, ADR-0028): access of client contacts to the portal and what the portal
// shows per organisation. REST only: the portal publishes no MCP tools (docs/01-architectuur/05-api-en-mcp.md).

export const ACCESS_ROLES = ['member', 'admin'] as const;
export const PORTAL_MODULES = ['projects', 'invoices', 'proposals', 'tickets', 'issues', 'articles', 'hours'] as const;
export const LOGIN_METHODS = ['entra', 'email_code'] as const;
export const ACCESS_STATUSES = ['invited', 'active', 'revoked'] as const;
export const ISSUE_VISIBILITY = ['none', 'flagged'] as const;
/** An invitation link works this many days after it is sent. */
export const INVITE_DAYS = 14;

const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');
const Modules = z.array(z.enum(PORTAL_MODULES)).max(PORTAL_MODULES.length).describe('Portal modules: projects, invoices, proposals, tickets, issues, articles, hours');
const Role = z.enum(ACCESS_ROLES).describe('member: own tickets; admin: all tickets of the organisation (CO-0662)');
const LoginMethod = z.enum(LOGIN_METHODS).describe('entra: own Entra account with MFA; email_code: a one-time code by mail, for contacts without a Microsoft account (ADR-0028)');

export const PortalAccess = z
  .object({
    id: Id,
    contactId: Id,
    contactName: z.string(),
    email: z.string().nullable(),
    organisationId: Id,
    userId: Id,
    role: Role,
    modules: Modules,
    loginMethod: LoginMethod,
    status: z.enum(ACCESS_STATUSES),
    invitation: z.enum(['none', 'waiting', 'sent', 'expired']).describe('waiting: the mail waits for approval; sent: the link works; expired: send a new one'),
    invitedAt: IsoDateTime.nullable(),
    inviteExpiresAt: IsoDateTime.nullable(),
    activatedAt: IsoDateTime.nullable(),
    lastLoginAt: IsoDateTime.nullable(),
    revokedAt: IsoDateTime.nullable(),
    revokeReason: z.string().nullable(),
    version: z.number().int(),
  })
  .openapi('PortalAccess');

export const PortalOrganisation = z
  .object({
    organisationId: Id,
    enabled: z.boolean(),
    modules: Modules,
    issueVisibility: z.enum(ISSUE_VISIBILITY).describe('none, or flagged: issues with the app field client visible (CO-0665)'),
    version: z.number().int().describe('0 when the organisation has no portal settings yet'),
  })
  .openapi('PortalOrganisation');

// ---------------------------------------------------------------- for staff

export const listAccess = defineAction({
  name: 'portal.access.list',
  method: 'get',
  path: '/portal/access',
  permission: P.keys.manage,
  input: z.object({ organisationId: Id }),
  output: z.object({ items: z.array(PortalAccess) }),
  summary: 'The contacts of an organisation with portal access (CO-0662)',
  restOnly: true,
});

export const inviteAccess = defineAction({
  name: 'portal.access.invite',
  method: 'post',
  path: '/portal/access',
  permission: P.keys.manage,
  input: z
    .object({
      contactId: Id,
      organisationId: Id.optional().describe('The organisation of the portal: one of the contact (CO-1363). Default: its primary organisation'),
      role: Role.default('member'),
      modules: Modules.optional().describe('Default: the modules of the organisation'),
      loginMethod: LoginMethod.default('entra'),
      message: z.string().trim().max(2000).optional().describe('A line of the inviter above the standard text'),
    })
    .strict(),
  output: PortalAccess,
  summary: 'Give a contact portal access: makes the client user and an invitation whose mail waits for approval (CO-0662, CO-0033, CO-0034)',
  restOnly: true,
});

export const updateAccess = defineAction({
  name: 'portal.access.update',
  method: 'patch',
  path: '/portal/access/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, role: Role.optional(), modules: Modules.optional(), loginMethod: LoginMethod.optional(), expectedVersion: ExpectedVersion }).strict(),
  output: PortalAccess,
  summary: 'Change the role, modules or login method of a portal access (CO-0662)',
  etag: true,
  restOnly: true,
});

export const reinviteAccess = defineAction({
  name: 'portal.access.reinvite',
  method: 'post',
  path: '/portal/access/{id}/invite',
  permission: P.keys.manage,
  input: z.object({ id: Id, message: z.string().trim().max(2000).optional() }).strict(),
  output: PortalAccess,
  summary: 'Send a new invitation link, again through approval; also gives revoked access back (CO-0662)',
  restOnly: true,
});

export const revokeAccess = defineAction({
  name: 'portal.access.revoke',
  method: 'post',
  path: '/portal/access/{id}/revoke',
  permission: P.keys.manage,
  input: z.object({ id: Id, reason: z.string().trim().min(1).max(500) }).strict(),
  output: PortalAccess,
  summary: 'End the portal access of a contact at once: sessions end and the grant goes (CO-0662)',
  restOnly: true,
});

export const getOrganisation = defineAction({
  name: 'portal.organisation.get',
  method: 'get',
  path: '/portal/organisations/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id }),
  output: PortalOrganisation,
  summary: 'What the portal shows of an organisation (CO-0665)',
  restOnly: true,
});

export const updateOrganisation = defineAction({
  name: 'portal.organisation.update',
  method: 'put',
  path: '/portal/organisations/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, enabled: z.boolean(), modules: Modules, issueVisibility: z.enum(ISSUE_VISIBILITY).default('none') }).strict(),
  output: PortalOrganisation,
  summary: 'Turn the portal on or off for an organisation and choose its modules (CO-0665)',
  restOnly: true,
});

export const PortalEngagement = z
  .object({
    engagementId: Id,
    organisationId: Id,
    showUsage: z.boolean().describe('The use of the retainer or the budget'),
    showBilledHours: z.boolean().describe('The billed hours per month'),
    showTimeEntries: z.boolean().describe('The time entries: date, person, hours, description; never rates or cost'),
    version: z.number().int().describe('0 when the engagement has no portal settings yet'),
  })
  .openapi('PortalEngagement');

export const getEngagement = defineAction({
  name: 'portal.engagement.get',
  method: 'get',
  path: '/portal/engagements/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id }),
  output: PortalEngagement,
  summary: 'What the client sees of the hours of an engagement in the portal (CO-1352)',
  restOnly: true,
});

export const updateEngagement = defineAction({
  name: 'portal.engagement.update',
  method: 'put',
  path: '/portal/engagements/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, showUsage: z.boolean(), showBilledHours: z.boolean(), showTimeEntries: z.boolean() }).strict(),
  output: PortalEngagement,
  summary: 'Choose what the client sees of the hours of an engagement: usage, billed hours, time entries (CO-1352)',
  restOnly: true,
});

// ---------------------------------------------------------------- for the client

export const PortalMe = z
  .object({
    accessId: Id,
    contactId: Id,
    name: z.string(),
    email: z.string().nullable(),
    organisationId: Id,
    organisationName: z.string(),
    role: Role,
    modules: Modules.describe('What this contact can use: its own modules within those of the organisation'),
    loginMethod: LoginMethod,
  })
  .openapi('PortalMe');

export const getMe = defineAction({
  name: 'portal.me.get',
  method: 'get',
  path: '/portal/me',
  permission: P.keys.read,
  input: z.object({}),
  output: PortalMe,
  summary: 'The contact, organisation, role and modules of the client user (CO-0661)',
  restOnly: true,
});

export const PortalTodo = z.object({
  kind: z.enum(['proposal', 'milestone', 'task', 'invoice', 'ticket']),
  id: Id,
  title: z.string(),
  detail: z.string().nullable(),
  dueOn: IsoDate.nullable().describe('Task: due; proposal: valid until; invoice: due date'),
  path: z.string().describe('The page in the portal'),
});

export const getHome = defineAction({
  name: 'portal.home.get',
  method: 'get',
  path: '/portal/home',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ me: PortalMe, todo: z.array(PortalTodo).describe('What waits for the client, newest first') }),
  summary: 'The start page of the portal: who you are and what waits for you (CO-0661)',
  restOnly: true,
});

// ---------------------------------------------------------------- tickets (CO-0660) and articles (CO-0497)

export const TICKET_STATES = ['open', 'waiting', 'solved', 'closed'] as const;
const Priority = z.enum(['urgent', 'high', 'normal', 'low']).describe('How urgent it is for you');

export const PortalTicket = z
  .object({
    id: Id,
    key: z.string().describe('S-00042'),
    subject: z.string(),
    status: z.enum(TICKET_STATES).describe('waiting: we wait for your answer'),
    type: z.enum(['incident', 'request']),
    priority: Priority,
    projectId: Id.nullable(),
    projectName: z.string().nullable(),
    requesterName: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    solvedAt: IsoDateTime.nullable(),
  })
  .openapi('PortalTicket');

export const PortalTicketDetail = PortalTicket.extend({
  description: z.string(),
  messages: z.array(
    z.object({
      id: Id,
      fromClient: z.boolean(),
      author: z.string(),
      body: z.string(),
      at: IsoDateTime,
      files: z.array(z.object({ fileId: Id, name: z.string(), size: z.number().int() })).describe('Download: GET /portal/tickets/{id}/files/{fileId}'),
    }),
  ),
}).openapi('PortalTicketDetail');

const FileIds = z.array(Id).max(10).optional().describe('Files uploaded first with PUT /portal/files');

export const listTickets = defineAction({
  name: 'portal.ticket.list',
  method: 'get',
  path: '/portal/tickets',
  permission: P.keys.read,
  input: z.object({ state: z.enum(['open', 'closed']).optional() }),
  output: z.object({ items: z.array(PortalTicket) }),
  summary: 'The tickets of the client: a member its own, an admin all of the organisation (CO-0661, CO-0662)',
  restOnly: true,
});

export const getTicket = defineAction({
  name: 'portal.ticket.get',
  method: 'get',
  path: '/portal/tickets/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: PortalTicketDetail,
  summary: 'A ticket with its messages to and from the client, without internal notes (CO-0661)',
  restOnly: true,
});

export const createTicket = defineAction({
  name: 'portal.ticket.create',
  method: 'post',
  path: '/portal/tickets',
  permission: P.keys.submit,
  input: z
    .object({
      subject: z.string().trim().min(1).max(300),
      body: z.string().trim().min(1).max(50_000),
      type: z.enum(['incident', 'request']).default('incident'),
      priority: Priority.default('normal'),
      projectId: Id.nullable().optional(),
      fileIds: FileIds,
    })
    .strict(),
  output: PortalTicketDetail,
  summary: 'Report an incident or request: channel web, with the SLA of the engagement (CO-0660)',
  restOnly: true,
});

export const replyTicket = defineAction({
  name: 'portal.ticket.reply',
  method: 'post',
  path: '/portal/tickets/{id}/messages',
  permission: P.keys.submit,
  input: z.object({ id: Id, body: z.string().trim().min(1).max(50_000), fileIds: FileIds }).strict(),
  output: PortalTicketDetail,
  summary: 'Answer on a ticket; a ticket that waited or was solved is open again (CO-0660)',
  restOnly: true,
});

export const PortalArticle = z
  .object({ id: Id, title: z.string(), body: z.string().describe('Markdown'), excerpt: z.string(), category: z.string().nullable(), updatedAt: IsoDateTime })
  .openapi('PortalArticle');

export const listArticles = defineAction({
  name: 'portal.article.list',
  method: 'get',
  path: '/portal/articles',
  permission: P.keys.read,
  input: z.object({ q: z.string().max(200).optional() }),
  output: z.object({ items: z.array(PortalArticle) }),
  summary: 'The knowledge articles for this client and for every client (CO-0497)',
  restOnly: true,
});

export const getArticle = defineAction({
  name: 'portal.article.get',
  method: 'get',
  path: '/portal/articles/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: PortalArticle,
  summary: 'A knowledge article for this client (CO-0497)',
  restOnly: true,
});

// ---------------------------------------------------------------- invoices (CO-0661) and hours (CO-1352)

export const PortalInvoice = z
  .object({
    id: Id,
    kind: z.enum(['invoice', 'credit']),
    number: z.string(),
    issueDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    currency: z.string(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    openCents: z.number().int().describe('What is still to be paid'),
    state: z.enum(['open', 'overdue', 'paid', 'credited', 'credit']),
    reference: z.string().nullable(),
    credits: z.string().nullable().describe('The number of the invoice a credit note corrects'),
    hasSpecification: z.boolean(),
    hasUbl: z.boolean(),
  })
  .openapi('PortalInvoice');

export const listInvoices = defineAction({
  name: 'portal.invoice.list',
  method: 'get',
  path: '/portal/invoices',
  permission: P.keys.read,
  input: z.object({ state: z.enum(['open', 'all']).optional() }),
  output: z.object({ items: z.array(PortalInvoice), openCents: z.number().int(), overdueCents: z.number().int() }),
  summary: 'The invoices and credit notes sent to the client, with what is open; documents at /portal/invoices/{id}/pdf, /ubl and /specification (CO-0661)',
  restOnly: true,
});

const Seconds = z.number().int();

export const HoursEngagement = z
  .object({ id: Id, name: z.string(), billingModel: z.string(), showUsage: z.boolean(), showBilledHours: z.boolean(), showTimeEntries: z.boolean() })
  .openapi('PortalHoursEngagement');

export const listHours = defineAction({
  name: 'portal.usage.list',
  method: 'get',
  path: '/portal/usage',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(HoursEngagement) }),
  summary: 'The engagements whose hours the client may see, and what of them (CO-1352)',
  restOnly: true,
});

export const PortalUsage = z
  .object({
    engagement: HoursEngagement,
    retainer: z
      .object({
        retainerPeriod: z.string(),
        allowanceSeconds: Seconds,
        rollover: z.boolean(),
        items: z.array(z.object({ periodFrom: z.string(), periodTo: z.string(), allowanceSeconds: Seconds, carriedInSeconds: Seconds, usedSeconds: Seconds, remainingSeconds: Seconds, overageSeconds: Seconds, complete: z.boolean(), invoiceNumber: z.string().nullable() })),
      })
      .nullable()
      .describe('The use per period of a retainer, newest first; with showUsage'),
    budget: z
      .object({ budgetSeconds: Seconds.nullable(), spentSeconds: Seconds, budgetCents: z.number().int().nullable(), spentCents: z.number().int(), currency: z.string() })
      .nullable()
      .describe('The use of the budget of another engagement; with showUsage'),
    billed: z.array(z.object({ month: z.string(), seconds: Seconds })).nullable().describe('Billed hours per month in the period; with showBilledHours'),
    entries: z
      .object({
        items: z.array(z.object({ date: z.string(), person: z.string(), seconds: Seconds, description: z.string(), project: z.string().nullable(), billable: z.boolean(), billed: z.boolean() })),
        nextCursor: z.string().nullable(),
      })
      .nullable()
      .describe('The time entries in the period, newest first; with showTimeEntries'),
  })
  .openapi('PortalUsage');

export const getHours = defineAction({
  name: 'portal.usage.get',
  method: 'get',
  path: '/portal/usage/{id}',
  permission: P.keys.read,
  input: z.object({
    id: Id,
    from: IsoDate.optional().describe('Default the first day of the month two months ago'),
    to: IsoDate.optional().describe('Default today; at most 366 days after from'),
    cursor: z.string().max(500).optional(),
  }),
  output: PortalUsage,
  summary: 'What the client sees of the hours of an engagement: use of the retainer or budget, billed hours, time entries; never rates or cost (CO-1352)',
  restOnly: true,
});

// ---------------------------------------------------------------- proposals (CO-0663)

const ProposalState = z.enum(['open', 'accepted', 'rejected', 'expired']).describe('open: the client can still decide');

export const PortalProposal = z
  .object({
    id: Id.describe('The version'),
    number: z.string(),
    title: z.string(),
    version: z.number().int(),
    state: ProposalState,
    validUntil: z.string().nullable(),
    totalCents: z.number().int(),
    currency: z.string(),
    sentAt: IsoDateTime.nullable(),
    decidedAt: IsoDateTime.nullable(),
    viewed: z.boolean(),
  })
  .openapi('PortalProposal');

export const PortalProposalDetail = z
  .object({
    id: Id,
    number: z.string(),
    title: z.string(),
    version: z.number().int(),
    state: ProposalState,
    validUntil: z.string().nullable(),
    currency: z.string(),
    intro: z.string(),
    scope: z.string(),
    terms: z.string(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    hasPdf: z.boolean().describe('The PDF: GET /portal/proposals/{id}/pdf'),
    lines: z.array(
      z.object({
        id: Id,
        kind: z.enum(['item', 'option', 'alternative']).describe('item: always; option: when chosen; alternative: one of its group'),
        groupKey: z.string().nullable(),
        selected: z.boolean(),
        phase: z.string().nullable(),
        description: z.string(),
        quantity: z.number(),
        unit: z.string(),
        unitPriceCents: z.number().int(),
        amountCents: z.number().int(),
        vatRateBp: z.number().int(),
      }),
    ),
    decision: z.object({ decision: z.enum(['accepted', 'rejected']), at: IsoDateTime, byName: z.string().nullable(), viaPortal: z.boolean() }).nullable(),
  })
  .openapi('PortalProposalDetail');

export const listProposals = defineAction({
  name: 'portal.proposal.list',
  method: 'get',
  path: '/portal/proposals',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(PortalProposal) }),
  summary: 'The proposals sent to the client, the newest version of each (CO-0663)',
  restOnly: true,
});

export const getProposal = defineAction({
  name: 'portal.proposal.get',
  method: 'get',
  path: '/portal/proposals/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: PortalProposalDetail,
  summary: 'A sent proposal with its lines, options and alternatives; the first opening is recorded (CO-0663)',
  restOnly: true,
});

export const decideProposal = defineAction({
  name: 'portal.proposal.decide',
  method: 'post',
  path: '/portal/proposals/{id}/decision',
  permission: P.keys.submit,
  input: z
    .object({
      id: Id,
      decision: z.enum(['accepted', 'rejected']),
      selectedLineIds: z.array(Id).max(500).optional().describe('On accepting: the chosen options and one alternative per group; items always count'),
      signedName: z.string().trim().min(2).max(200).describe('Your full name as signature'),
      note: z.string().trim().max(2000).optional(),
    })
    .strict(),
  output: PortalProposalDetail,
  successStatus: 200,
  summary: 'Accept or decline a sent proposal: name, time and address are recorded and the proposal is locked (CO-0663)',
  restOnly: true,
});

// ---------------------------------------------------------------- projects (CO-0661, CO-0665, CO-0666)

const Health = z.enum(['on_track', 'at_risk', 'behind']).nullable();

export const PortalProject = z
  .object({
    id: Id,
    name: z.string(),
    status: z.string(),
    health: Health,
    startsOn: z.string().nullable(),
    endsOn: z.string().nullable(),
    milestonesToDecide: z.number().int(),
    openClientTasks: z.number().int(),
    latestUpdate: z.object({ week: z.string(), health: z.string(), summary: z.string() }).nullable().describe('The latest weekly status MKC shared'),
  })
  .openapi('PortalProject');

export const PortalProjectDetail = z
  .object({
    id: Id,
    name: z.string(),
    status: z.string(),
    health: Health,
    startsOn: z.string().nullable(),
    endsOn: z.string().nullable(),
    milestones: z.array(
      z.object({
        id: Id,
        title: z.string(),
        description: z.string(),
        dueOn: z.string().nullable(),
        status: z.enum(['planned', 'active', 'reached']),
        accepted: z.object({ at: IsoDateTime, byName: z.string().nullable(), viaPortal: z.boolean() }).nullable(),
        rejected: z.object({ at: IsoDateTime, byName: z.string().nullable(), note: z.string() }).nullable(),
        canDecide: z.boolean().describe('Delivered and not accepted yet'),
      }),
    ),
    tasks: z.array(
      z.object({ id: Id, key: z.string(), title: z.string(), description: z.string(), dueOn: z.string().nullable(), done: z.boolean(), doneAt: IsoDateTime.nullable(), clientNote: z.string(), doneByName: z.string().nullable() }),
    ),
    updates: z.array(z.object({ week: z.string(), health: z.string(), summary: z.string() })),
  })
  .openapi('PortalProjectDetail');

export const listProjects = defineAction({
  name: 'portal.project.list',
  method: 'get',
  path: '/portal/projects',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(PortalProject) }),
  summary: 'The projects of the client with status, health and what waits for it (CO-0661)',
  restOnly: true,
});

export const getProject = defineAction({
  name: 'portal.project.get',
  method: 'get',
  path: '/portal/projects/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: PortalProjectDetail,
  summary: 'A project of the client: milestones with their acceptance, tasks for the client and the shared weekly statuses (CO-0661, CO-0666)',
  restOnly: true,
});

export const decideMilestone = defineAction({
  name: 'portal.milestone.decide',
  method: 'post',
  path: '/portal/milestones/{id}/decision',
  permission: P.keys.submit,
  input: z
    .object({
      id: Id,
      decision: z.enum(['accept', 'reject']),
      comment: z.string().trim().max(5000).optional().describe('Required on reject: what is still missing'),
    })
    .strict(),
  output: PortalProjectDetail,
  successStatus: 200,
  summary: 'Accept or turn down a delivered milestone: name, time and address are recorded; accepting locks it and starts its invoice term (CO-0666)',
  restOnly: true,
});

export const completeTask = defineAction({
  name: 'portal.task.complete',
  method: 'post',
  path: '/portal/tasks/{id}/complete',
  permission: P.keys.submit,
  input: z.object({ id: Id, comment: z.string().trim().max(5000).optional() }).strict(),
  output: PortalProjectDetail,
  successStatus: 200,
  summary: 'Finish a task for the client, such as an acceptance test, with a comment (CO-0666)',
  restOnly: true,
});

export const PortalIssue = z
  .object({ id: Id, ref: z.string(), title: z.string(), state: z.enum(['open', 'closed']), milestone: z.string().nullable(), updatedAt: IsoDateTime, closedAt: IsoDateTime.nullable() })
  .openapi('PortalIssue');

export const listIssues = defineAction({
  name: 'portal.issue.list',
  method: 'get',
  path: '/portal/issues',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(PortalIssue) }),
  summary: 'The issues MKC marked client visible, of the repositories of the client (CO-0665)',
  restOnly: true,
});

// ---------------------------------------------------------------- events

export const accessChanged = defineEvent({
  type: 'portal.access.changed',
  version: 1,
  payload: z.object({ accessId: Id, contactId: Id, organisationId: Id, userId: Id, change: z.enum(['invited', 'updated', 'sent', 'active', 'revoked']) }),
});

export const portalEvents = [accessChanged];

export const portalActions = [listAccess, inviteAccess, updateAccess, reinviteAccess, revokeAccess, getOrganisation, updateOrganisation, getEngagement, updateEngagement, getMe, getHome, listTickets, getTicket, createTicket, replyTicket, listArticles, getArticle, listInvoices, listHours, getHours, listProposals, getProposal, decideProposal, listProjects, getProject, decideMilestone, completeTask, listIssues];
